import { AppUserSubscriptionTier } from "@/core/common/types";
import { getTeamOrUserQuotas } from "@/hooks/use-user-quotas";
import { useUserHighestStripeSubscription } from "@/hooks/use-user-stripe-subscription-update";
import classNames from "classnames";
import { Download } from "lucide-react";
import { useCallback } from "react";
import { useManageSubscriptionDialogContext } from "../popup/message-dialog/manage-subscription-dialog";
import { PrimaryButton } from "../primitives/button/button";
import { Tooltip } from "../utils/tooltip";

/**
 * Returns whether downloads are disabled due to the user's subscription plan.
 * TODO: Limit Downloads by quota, and apply logic here.
 * @returns {
 *   downloadsDisabled: boolean;
 *   currentSubscription: AppSubscriptionPlanType;
 * }
 */
export function useCanDownloadAssets() {
  const currentSubscription = useUserHighestStripeSubscription();
  const userQuotas = getTeamOrUserQuotas();
  const canDownloadAssets =
    userQuotas?.tier === AppUserSubscriptionTier.Pro ||
    userQuotas?.tier === AppUserSubscriptionTier.Enterprise;

  return {
    downloadsDisabled: !canDownloadAssets,
    currentSubscription,
  };
}
/**
 * Download button that is controlled by useCanDownloadAssets() hook.
 */
export function ControlledDownloadButton({
  activeImageUrl,
  disabled,
  icon = <Download size={16} />,
  className,
  onClick,
  children,
  tooltipContent,
}: {
  activeImageUrl: string;
  disabled?: boolean;
  icon?: React.ReactNode;
  className?: string;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  children?: React.ReactNode;
  tooltipContent?: string;
}) {
  const { downloadsDisabled } = useCanDownloadAssets();
  const { setOpen } = useManageSubscriptionDialogContext();

  const isDisabled = !activeImageUrl || downloadsDisabled || disabled;
  const clickHandler = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      if (!isDisabled && onClick) {
        return onClick(event);
      }
      //pop up the upgrade modal
      return setOpen(true);
    },
    [isDisabled, onClick, setOpen],
  );

  const defaultTooltip = downloadsDisabled ? "Upgrade to download images" : "Download image";
  const displayTooltip = tooltipContent || defaultTooltip;

  return (
    <Tooltip contentChildren={displayTooltip}>
      <PrimaryButton
        onClick={clickHandler}
        className={classNames(
          "flex flex-row items-center justify-center gap-2",
          className,
          isDisabled && "hover:cursor-not-allowed",
        )}
      >
        {icon}
        {children}
      </PrimaryButton>
    </Tooltip>
  );
}
